import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import GetAppIcon from '@mui/icons-material/GetApp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import * as XLSX from 'xlsx';
import Parse from 'parse';
import SetOperatorRtpDialog from './dialog/SetOperatorRtpDialog';
import DeleteOperatorGameConfigDialog from './dialog/DeleteOperatorGameConfigDialog';
import CustomPagination from '../../components/CustomPagination';

const OperatorGamesList = () => {
  // ═══ DATA STATE ═══════════════════════════════════════════════════
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operators, setOperators] = useState([]);

  // ═══ FILTERS & SEARCH ════════════════════════════════════════════
  const [searchQuery, setSearchQuery] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [selectedOperatorFilter, setSelectedOperatorFilter] = useState('');
  const debounceRef = useRef(null);

  // ═══ DIALOGS ═════════════════════════════════════════════════════
  const [setRtpDialogOpen, setSetRtpDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedAuditConfig, setSelectedAuditConfig] = useState(null);
  const [expandedOperators, setExpandedOperators] = useState({});
  const [dialogMode, setDialogMode] = useState('create');

  // ═══ SNACKBAR ════════════════════════════════════════════════════
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ═══ FETCH OPERATORS ════════════════════════════════════════════
  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const result = await Parse.Cloud.run('rgsListOperatorsForDropdown');
        if (result.success) {
          setOperators(result.operators);
        }
      } catch (error) {
        console.error('Error fetching operators:', error);
      }
    };
    fetchOperators();
  }, []);

  // ═══ DEBOUNCED SEARCH ═══════════════════════════════════════════
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearchQuery(localSearch);
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localSearch]);

  // ═══ FETCH CONFIGS ══════════════════════════════════════════════
  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: 10000,
        page: 1
      };
      if (selectedOperatorFilter) {
        params.operatorId = selectedOperatorFilter;
      }

      const result = await Parse.Cloud.run('rgsListAllOperatorGameConfigs', params);
      if (result.success) {
        setConfigs(result.configs);
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to fetch configs',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [selectedOperatorFilter]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // ═══ CLIENT-SIDE FILTERING ══════════════════════════════════════
  const filteredConfigs = useMemo(() => {
    let filtered = [...configs];

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.operatorName?.toLowerCase().includes(q) ||
          c.gameSlug?.toLowerCase().includes(q) ||
          c.modeName?.toLowerCase().includes(q) ||
          String(c.rtpLevel)?.includes(q)
      );
    }

    return filtered;
  }, [configs, searchQuery]);

  // ═══ HANDLERS ═══════════════════════════════════════════════════
  const handleExport = async () => {
    try {
      const params = { limit: 10000, page: 1 };
      if (selectedOperatorFilter) params.operatorId = selectedOperatorFilter;

      const result = await Parse.Cloud.run('rgsListAllOperatorGameConfigs', params);
      if (!result.success || !result.configs?.length) {
        setSnackbar({ open: true, message: 'No data to export', severity: 'warning' });
        return;
      }

      const exportData = result.configs.map((c) => ({
        'Operator Name': c.operatorName,
        'Game Slug': c.gameSlug,
        'Game Mode': c.modeName || '-',
        'Configured RTP Level': c.rtpLevel || '-',
        'Configured RTP (%)': c.rtp || '-',
        'Current RTP (%)': c.currentRtp != null ? Number(c.currentRtp).toFixed(2) : '-',
        'Spins Completed': c.spinCount ?? 0,
        Active: c.isActive ? 'Yes' : 'No',
        'Updated At': new Date(c.updatedAt).toLocaleString(),
        'Created At': new Date(c.createdAt).toLocaleString()
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Operator Games');
      XLSX.writeFile(wb, `operator_games_${new Date().toISOString().split('T')[0]}.xlsx`);
      setSnackbar({ open: true, message: 'Data exported successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to export data', severity: 'error' });
    }
  };

  const handleCreateClick = () => {
    setSelectedConfig(null);
    setDialogMode('create');
    setSetRtpDialogOpen(true);
  };

  const handleEditClick = (config) => {
    setSelectedConfig(config);
    setDialogMode('edit');
    setSetRtpDialogOpen(true);
  };

  const handleDeleteClick = (config) => {
    setSelectedConfig(config);
    setDeleteDialogOpen(true);
  };

  const handleOpenAudit = (config) => {
    setExpandedOperators((prev) => ({ ...prev, [config.operatorId]: true }));
    setSelectedAuditConfig(config);
  };

  const handleSaved = () => {
    setSnackbar({
      open: true,
      message:
        dialogMode === 'create'
          ? 'Game RTP assigned successfully'
          : 'Game RTP updated successfully',
      severity: 'success'
    });
    fetchConfigs();
  };

  const handleDeleted = () => {
    setSnackbar({ open: true, message: 'Game config deleted successfully', severity: 'success' });
    fetchConfigs();
  };

  if (selectedAuditConfig) {
    return (
      <SpinAuditLogView config={selectedAuditConfig} onBack={() => setSelectedAuditConfig(null)} />
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: '#1A1A1A', minHeight: '100vh' }}>
      {/* ═══ SEARCH & FILTER TOOLBAR ══════════════════════════════════ */}
      <Box
        sx={{
          backgroundColor: '#242424',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.08)',
          p: 2,
          mb: 2.5,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search Bar */}
          <TextField
            placeholder="Search..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            size="small"
            sx={{
              minWidth: 200,
              backgroundColor: '#2b2b2b',
              borderRadius: '4px',
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { border: 'none' }
              }
            }}
          />

          {/* Operator Filter Dropdown */}
          <Box sx={{ position: 'relative' }}>
            <Typography
              sx={{
                position: 'absolute',
                top: -18,
                left: 0,
                color: 'rgba(255,255,255,0.7)',
                fontSize: '12px'
              }}
            >
              Operator
            </Typography>
            <Select
              value={selectedOperatorFilter}
              onChange={(e) => setSelectedOperatorFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 200,
                backgroundColor: '#2b2b2b',
                color: '#fff',
                borderRadius: '4px',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiSvgIcon-root': { color: '#fff' }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#2b2b2b',
                    color: '#fff'
                  }
                }
              }}
            >
              <MenuItem value="">All</MenuItem>
              {operators.map((op) => (
                <MenuItem key={op.id} value={op.id}>
                  {op.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#FFC107',
              color: '#000',
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              '&:hover': { backgroundColor: '#FFB300' }
            }}
          >
            Assign Game RTP
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            startIcon={<GetAppIcon />}
            sx={{
              backgroundColor: '#FFC107',
              color: '#000',
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              '&:hover': { backgroundColor: '#FFB300' }
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* ═══ GROUPED OPERATOR VIEW ════════════════════════════════════ */}
      <OperatorGroupedView
        configs={filteredConfigs}
        loading={loading}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        onOpenAudit={handleOpenAudit}
        expandedOperators={expandedOperators}
        setExpandedOperators={setExpandedOperators}
      />

      {/* ═══ DIALOGS ══════════════════════════════════════════════════ */}
      <SetOperatorRtpDialog
        open={setRtpDialogOpen}
        onClose={() => setSetRtpDialogOpen(false)}
        onSave={handleSaved}
        config={selectedConfig}
        mode={dialogMode}
        operators={operators}
      />

      <DeleteOperatorGameConfigDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={handleDeleted}
        config={selectedConfig}
      />

      {/* ═══ SNACKBAR ════════════════════════════════════════════════ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// OPERATOR GROUPED VIEW
// ═══════════════════════════════════════════════════════════════════════════

const OperatorGroupedView = ({
  configs,
  loading,
  onEdit,
  onDelete,
  onOpenAudit,
  expandedOperators,
  setExpandedOperators
}) => {
  const groupedByOperator = useMemo(() => {
    const groups = {};
    configs.forEach((c) => {
      if (!groups[c.operatorId]) {
        groups[c.operatorId] = {
          operatorId: c.operatorId,
          operatorName: c.operatorName,
          configs: []
        };
      }
      groups[c.operatorId].configs.push(c);
    });
    return Object.values(groups);
  }, [configs]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#FFC107' }} />
      </Box>
    );
  }

  if (groupedByOperator.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: 'rgba(255,255,255,0.4)' }}>
        <Typography variant="h6">No game configs found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {groupedByOperator.map((group) => {
        const isExpanded = expandedOperators[group.operatorId];
        const activeCount = group.configs.filter((c) => c.isActive).length;

        return (
          <Box
            key={group.operatorId}
            sx={{
              backgroundColor: '#242424',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.08)',
              overflow: 'hidden',
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }
            }}
          >
            {/* Operator Card Header */}
            <Box
              onClick={() =>
                setExpandedOperators((prev) => ({
                  ...prev,
                  [group.operatorId]: !prev[group.operatorId]
                }))
              }
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
                backgroundColor: 'rgba(255,255,255,0.02)',
                '&:hover': { backgroundColor: 'rgba(255,193,7,0.08)' },
                transition: 'background-color 0.2s'
              }}
            >
              {/* Avatar */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                  backgroundColor: '#FFC107',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  color: '#000',
                  fontSize: '14px'
                }}
              >
                {group.operatorName.substring(0, 2).toUpperCase()}
              </Box>

              {/* Operator Info */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, color: '#fff' }}>
                  {group.operatorName}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                  {group.operatorId}
                </Typography>
              </Box>

              {/* Stats Chips */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`${group.configs.length} games`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(33,150,243,0.15)',
                    color: '#90CAF9',
                    fontSize: '12px'
                  }}
                />
                <Chip
                  label={`${activeCount} active`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(76,175,80,0.15)',
                    color: '#A5D6A7',
                    fontSize: '12px'
                  }}
                />
              </Box>

              {/* Expand Icon */}
              <IconButton
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Expanded Games Table */}
            {isExpanded && (
              <Box
                sx={{
                  p: 2,
                  borderTop: '1px solid rgba(255,255,255,0.05)',
                  backgroundColor: '#1A1A1A'
                }}
              >
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <Box component="thead">
                    <Box component="tr">
                      {[
                        'Game',
                        'Mode',
                        'Configured RTP',
                        'Current RTP',
                        'Spins',
                        'Status',
                        'Updated',
                        'Actions'
                      ].map((h) => (
                        <Box
                          component="th"
                          key={h}
                          sx={{
                            p: '8px 12px',
                            textAlign: 'left',
                            color: '#FFC107',
                            fontWeight: 600,
                            fontSize: '12px',
                            borderBottom: '1px solid rgba(255,255,255,0.08)'
                          }}
                        >
                          {h}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {group.configs.map((config) => (
                      <Box
                        component="tr"
                        key={config.id}
                        onClick={() => onOpenAudit(config)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' }
                        }}
                      >
                        <Box
                          component="td"
                          sx={{
                            p: '10px 12px',
                            fontSize: '13px',
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}
                        >
                          <Chip
                            label={config.gameSlug}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(255,193,7,0.15)',
                              color: '#FFC107',
                              fontWeight: 500
                            }}
                          />
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            p: '10px 12px',
                            fontSize: '13px',
                            color: '#fff',
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}
                        >
                          {config.modeName || '-'}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            p: '10px 12px',
                            fontSize: '13px',
                            color: '#FFC107',
                            fontWeight: 600,
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}
                        >
                          {config.rtpLevel || '-'}%
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            p: '10px 12px',
                            fontSize: '13px',
                            color: '#4CAF50',
                            fontWeight: 600,
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}
                        >
                          {config.currentRtp != null
                            ? `${Number(config.currentRtp).toFixed(2)}%`
                            : '—'}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            p: '10px 12px',
                            fontSize: '13px',
                            color: '#fff',
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}
                        >
                          {(config.spinCount ?? 0).toLocaleString()}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            p: '10px 12px',
                            fontSize: '13px',
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}
                        >
                          <Chip
                            label={config.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              backgroundColor: config.isActive
                                ? 'rgba(76,175,80,0.2)'
                                : 'rgba(244,67,54,0.2)',
                              color: config.isActive ? '#4CAF50' : '#F44336',
                              fontWeight: 500
                            }}
                          />
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            p: '10px 12px',
                            fontSize: '12px',
                            color: 'rgba(255,255,255,0.5)',
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}
                        >
                          {new Date(config.updatedAt).toLocaleDateString()}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            p: '10px 12px',
                            fontSize: '13px',
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onEdit(config);
                                }}
                                sx={{
                                  color: '#FFC107',
                                  '&:hover': { backgroundColor: 'rgba(255,193,7,0.15)' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onDelete(config);
                                }}
                                sx={{
                                  color: '#F44336',
                                  '&:hover': { backgroundColor: 'rgba(244,67,54,0.15)' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const SpinAuditLogView = ({ config, onBack }) => {
  const getTodayInputValue = () => {
    const today = new Date();
    const timezoneOffset = today.getTimezoneOffset() * 60000;
    return new Date(today.getTime() - timezoneOffset).toISOString().split('T')[0];
  };

  const todayInputValue = useMemo(() => getTodayInputValue(), []);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [startDate, setStartDate] = useState(todayInputValue);
  const [endDate, setEndDate] = useState(todayInputValue);
  const [filterVersion, setFilterVersion] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [spinResultPreview, setSpinResultPreview] = useState(null);

  const auditParams = useMemo(
    () => ({
      operatorId: config.operatorId,
      gameModeId: config.gameModeId,
      search: appliedSearch,
      startDate,
      endDate
    }),
    [config.operatorId, config.gameModeId, appliedSearch, startDate, endDate]
  );

  const fetchAuditRecords = useCallback(async () => {
    if (!config.operatorId || !config.gameModeId) return;

    setLoading(true);
    try {
      const result = await Parse.Cloud.run('rgsGetOperatorGameSpinAuditLog', {
        ...auditParams,
        page,
        limit: rowsPerPage
      });

      if (result.success) {
        setRecords(result.records || []);
        setTotal(result.total || 0);
      }
    } catch (error) {
      console.error('Error fetching spin audit records:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to fetch spin audit records',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [auditParams, config.operatorId, config.gameModeId, page, rowsPerPage, filterVersion]);

  useEffect(() => {
    fetchAuditRecords();
  }, [fetchAuditRecords]);

  const handleApplyFilters = () => {
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const handleClearFilters = () => {
    setSearch('');
    setAppliedSearch('');
    setStartDate('');
    setEndDate('');
    setPage(1);
    setFilterVersion((value) => value + 1);
  };

  const stringifySpinResult = (value) => {
    if (value == null) return '-';
    if (typeof value === 'string') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch (error) {
        return value;
      }
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return String(value);
    }
  };

  const formatAmount = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const handleExport = async () => {
    try {
      const result = await Parse.Cloud.run('rgsGetOperatorGameSpinAuditLog', {
        ...auditParams,
        page: 1,
        limit: 10000
      });

      const exportRows = result.records || [];
      if (!result.success || exportRows.length === 0) {
        setSnackbar({ open: true, message: 'No data to export', severity: 'warning' });
        return;
      }

      const ws = XLSX.utils.json_to_sheet(
        exportRows.map((row) => ({
          Username: row.username,
          'Spin ID': row.spinId,
          'Balance Before Spin': row.balanceBeforeSpin,
          'Bet Amount': row.betAmount,
          'Win Amount': row.winAmount,
          'Balance After Spin': row.balanceAfterSpin,
          'Spin Result': stringifySpinResult(row.spinResult),
          'Date & Time': row.dateTime
        }))
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Spin Audit Log');
      XLSX.writeFile(
        wb,
        `spin_audit_${config.operatorName}_${config.gameSlug}_${new Date().toISOString().split('T')[0]}.xlsx`
      );
      setSnackbar({ open: true, message: 'Audit log exported successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to export audit log', severity: 'error' });
    }
  };

  const columns = [
    'Username',
    'Spin ID',
    'Balance Before Spin',
    'Bet Amount',
    'Win Amount',
    'Balance After Spin',
    'Spin Result',
    'Date & Time'
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: '#1A1A1A', minHeight: '100vh' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title="Back">
            <IconButton
              onClick={onBack}
              sx={{
                color: '#FFC107',
                backgroundColor: '#242424',
                '&:hover': { backgroundColor: 'rgba(255,193,7,0.15)' }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography sx={{ color: '#FFC107', fontSize: '20px', fontWeight: 700 }}>
              Spin Audit Log
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px' }}>
              {config.operatorName} / {config.gameSlug}{' '}
              {config.modeName ? `/ ${config.modeName}` : ''}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={handleExport}
          startIcon={<GetAppIcon />}
          sx={{
            backgroundColor: '#FFC107',
            color: '#000',
            borderRadius: '4px',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#FFB300' }
          }}
        >
          Export
        </Button>
      </Box>

      <Box
        sx={{
          backgroundColor: '#242424',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.08)',
          p: 2,
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}
      >
        <TextField
          placeholder="Search Username or Spin ID"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleApplyFilters();
          }}
          size="small"
          sx={{
            minWidth: { xs: '100%', sm: 260 },
            backgroundColor: '#2b2b2b',
            borderRadius: '4px',
            '& .MuiOutlinedInput-root': {
              color: '#fff',
              '& fieldset': { border: 'none' }
            }
          }}
        />
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(event) => {
            setStartDate(event.target.value);
            setPage(1);
          }}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 160,
            backgroundColor: '#2b2b2b',
            borderRadius: '4px',
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.65)' },
            '& .MuiOutlinedInput-root': {
              color: '#fff',
              '& fieldset': { border: 'none' }
            },
            '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' }
          }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(event) => {
            setEndDate(event.target.value);
            setPage(1);
          }}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 160,
            backgroundColor: '#2b2b2b',
            borderRadius: '4px',
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.65)' },
            '& .MuiOutlinedInput-root': {
              color: '#fff',
              '& fieldset': { border: 'none' }
            },
            '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' }
          }}
        />
        <Button
          variant="contained"
          onClick={handleApplyFilters}
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: '#FFC107',
            color: '#000',
            borderRadius: '4px',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#FFB300' }
          }}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          onClick={handleClearFilters}
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: '#fff',
            textTransform: 'uppercase',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)'
            }
          }}
        >
          Clear Filters
        </Button>
      </Box>

      <Box
        sx={{
          backgroundColor: '#242424',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.08)',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 1120, tableLayout: 'fixed' }}
          >
            <Box component="thead">
              <Box component="tr">
                {columns.map((column) => (
                  <Box
                    component="th"
                    key={column}
                    sx={{
                      p: '10px 12px',
                      textAlign: 'center',
                      color: '#FFC107',
                      fontWeight: 700,
                      fontSize: '12px',
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                      whiteSpace: 'normal',
                      lineHeight: 1.3
                    }}
                  >
                    {column}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {loading ? (
                <Box component="tr">
                  <Box component="td" colSpan={columns.length} sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress sx={{ color: '#FFC107' }} />
                  </Box>
                </Box>
              ) : records.length === 0 ? (
                <Box component="tr">
                  <Box
                    component="td"
                    colSpan={columns.length}
                    sx={{ py: 7, textAlign: 'center', color: 'rgba(255,255,255,0.45)' }}
                  >
                    <Typography>No Data Found</Typography>
                  </Box>
                </Box>
              ) : (
                records.map((row) => (
                  <Box
                    component="tr"
                    key={row.id}
                    sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}
                  >
                    <Box component="td" sx={auditCellSx}>
                      {row.username}
                    </Box>
                    <Box component="td" sx={auditCellSx}>
                      {row.spinId}
                    </Box>
                    <Box component="td" sx={auditCellSx}>
                      {formatAmount(row.balanceBeforeSpin)}
                    </Box>
                    <Box component="td" sx={auditCellSx}>
                      {formatAmount(row.betAmount)}
                    </Box>
                    <Box component="td" sx={auditCellSx}>
                      {formatAmount(row.winAmount)}
                    </Box>
                    <Box component="td" sx={auditCellSx}>
                      {formatAmount(row.balanceAfterSpin)}
                    </Box>
                    <Box component="td" sx={auditCellSx}>
                      <IconButton
                        onClick={() =>
                          setSpinResultPreview({
                            username: row.username,
                            spinId: row.spinId,
                            result: row.spinResult
                          })
                        }
                        size="small"
                        sx={{
                          color: '#FFC107',
                          border: '1px solid rgba(255,193,7,0.35)',
                          borderRadius: 1,
                          '&:hover': { backgroundColor: 'rgba(255,193,7,0.08)' }
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box component="td" sx={{ ...auditCellSx, whiteSpace: 'nowrap' }}>
                      {row.dateTime}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>

        <CustomPagination
          page={page}
          perPage={rowsPerPage}
          total={total}
          setPage={setPage}
          setPerPage={setRowsPerPage}
        />
      </Box>

      <Dialog
        open={Boolean(spinResultPreview)}
        onClose={() => setSpinResultPreview(null)}
        PaperProps={{
          sx: {
            backgroundColor: '#2A2A2A',
            color: '#fff',
            minWidth: 640,
            maxWidth: '90vw'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFC107', fontWeight: 700 }}>
          Spin Result Preview
          {spinResultPreview?.spinId ? (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
              {spinResultPreview.spinId}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              backgroundColor: '#1E1E1E',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 1.5,
              color: '#FFC107',
              fontSize: '13px',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 420,
              overflowY: 'auto'
            }}
          >
            {stringifySpinResult(spinResultPreview?.result)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSpinResultPreview(null)}
            sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const auditCellSx = {
  p: '12px',
  fontSize: '13px',
  color: '#fff',
  borderBottom: '1px solid rgba(255,255,255,0.05)',
  textAlign: 'center',
  verticalAlign: 'middle',
  overflowWrap: 'anywhere'
};

export default OperatorGamesList;
